// 裝置清單「多選批次」核心純函式（APP#51 後續子項）。
//
// 對標 VistaFlow 多選批次；複用既有 per-device API（rename PATCH / share POST /
// delete DELETE / move-to-room 本地房間偏好），批次 = 對選取集逐台套用 + 收集
// 部分失敗（哪台失敗要能回報，不一台失敗就整批中止）。authz：管理型操作
// （rename/share/delete）owner-only，對混合選取只作用於可管理台、被擋的共享台
// 明列（server 仍為 fail-closed 權威，ADR-009）。純函式與 UI 解耦以利 vitest。

/** 單台批次結果彙整：succeeded=成功的 device_id；failed=失敗台 + 錯誤訊息。 */
export interface BatchOutcome {
  succeeded: string[];
  failed: { id: string; error: string }[];
}

/** owner / admin 才可管理（rename/share/delete）；對齊 DeviceSettings.tsx isOwner。 */
export function canManageDevice(role: string | undefined): boolean {
  return role === 'owner' || role === 'admin';
}

/**
 * 依可管理性切分選取集（保持 `devices` 原順序）。
 * - manageableIds：owner/admin 台（可 rename/share/delete）；
 * - blockedIds：選了但非 owner 的共享台（批次管理型操作略過，UI 需明示）。
 * 只納入 selected 命中且存在於 devices 的台。
 */
export function partitionSelection<T extends { device_id: string; role?: string }>(
  devices: T[],
  selected: ReadonlySet<string>
): { manageableIds: string[]; blockedIds: string[] } {
  const manageableIds: string[] = [];
  const blockedIds: string[] = [];
  for (const d of devices) {
    if (!selected.has(d.device_id)) continue;
    if (canManageDevice(d.role)) manageableIds.push(d.device_id);
    else blockedIds.push(d.device_id);
  }
  return { manageableIds, blockedIds };
}

/**
 * 批次改名的每台目標名（rename 不適合多台同名 → 前綴 + 編號）。
 * - total === 1：單台就用前綴原樣（等同單台改名）；
 * - total > 1：`${prefix} ${index + 1}`（1-based 序號，穩定跟 manageable 順序）。
 * prefix 已 trim；呼叫端負責長度/空白驗證。
 */
export function batchRenameName(prefix: string, index: number, total: number): string {
  return total <= 1 ? prefix : `${prefix} ${index + 1}`;
}

/** 擷取錯誤訊息（ApiError.message 優先，其餘退回 String(e)）。 */
function errText(e: unknown): string {
  if (e && typeof e === 'object' && 'message' in e && typeof (e as { message: unknown }).message === 'string') {
    return (e as { message: string }).message;
  }
  return e instanceof Error ? e.message : String(e);
}

/**
 * 對 `ids` 逐台套用 async `op`，收集部分失敗（不因單台失敗中止整批）。
 * 循序執行 → 序號穩定（供批次改名編號）、且不同時對後端猛打。
 * `op(id, index)`：index 為批次內位置（0-based）。
 */
export async function runBatch(
  ids: string[],
  op: (id: string, index: number) => Promise<void>
): Promise<BatchOutcome> {
  const succeeded: string[] = [];
  const failed: { id: string; error: string }[] = [];
  for (let i = 0; i < ids.length; i++) {
    const id = ids[i];
    try {
      await op(id, i);
      succeeded.push(id);
    } catch (e) {
      failed.push({ id, error: errText(e) });
    }
  }
  return { succeeded, failed };
}

/** 批次操作型別（供 UI 決定要跑哪個 per-device API）。 */
export type BatchAction = 'rename' | 'share' | 'delete' | 'move-group';
